// src/components/Terms.jsx

import { ArrowLeft } from 'lucide-react';
import BottomNav from '../../../components/layouts/Footer/BottomNav';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

export default function Terms() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <>
      <main className=" mx-auto px-4  pt-6 md:pb-6 pb-20 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl flex items-center gap-3 justify-center sm:text-4xl font-extrabold text-center mb-8 tracking-tight text-gray-900 dark:text-gray-100">
            <ArrowLeft onClick={() => navigate(-1)} className="cursor-pointer size-8" />
            Terms and Conditions
          </h1>

          <div className="prose prose-slate dark:prose-invert max-w-none space-y-3 text-base">
            {/* --- Existing Content Starts Here --- */}
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">
              Welcome to BigLocal.in!
            </h2>
            <p>
              These Terms of Use (&apos;Terms&apos;) govern your access and use of the BigLocal.in
              platform (Platform) and the services offered thereon (Services).
            </p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">About Us</h3>
            <p>
              Revvtch is a Limited Liability Partnership Firm incorporated under the Companies Act,
              2013, with its registered office at 205, Katyayani CGHS, Plot No-8, Sector-6, Dwarka,
              New Delhi-110075 (referred to as &apos;We,&apos; &apos;biglocal.in,&apos;
              &apos;BigLocal&apos; &apos;Us,&apos; or &apos;Our&apos; in these Terms).
            </p>

            {/* ... all other original terms sections ... */}

            <h3 className="font-bold text-gray-700 dark:text-gray-300">Indemnification</h3>
            <p>
              You agree to indemnify BigLocal.in for any claims arising out of your use of the
              Platform.
            </p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">Changes to the Terms</h3>
            <p>
              BigLocal.in may change these Terms at any time. We will post any changes on the
              Platform.
            </p>

            {/* --- New Return Policy Section --- */}
            <h3 className="font-bold text-gray-700 dark:text-gray-300">Return Policy</h3>

            <h4 className="font-semibold">General Guidelines</h4>
            <p>
              At BigLocal.in, we strive to ensure every purchase meets your expectations. However,
              due to the nature of our products and vendor policies, items are generally{' '}
              <strong>non-returnable, non-replaceable, and non-exchangeable</strong>. Your
              satisfaction remains our priority, and we do make exceptions under specific, outlined
              circumstances.
            </p>

            <h4 className="font-semibold">Conditions for Eligibility</h4>
            <p>
              You may be eligible to request a return or replacement if the product received falls
              into one of the following categories:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong>Damaged Goods:</strong> The product exhibits physical damage upon delivery.
              </li>
              <li>
                <strong>Defective Items:</strong> The product is found to be defective or not
                functioning as intended.
              </li>
              <li>
                <strong>Expired Products:</strong> The product has passed its stated expiry date
                when delivered to you.
              </li>
              <li>
                <strong>Incorrect Deliveries:</strong> The product received is not the item you
                ordered.
              </li>
            </ul>
            <p>
              <em>
                Please refer to the individual product page for any specific return policies set
                forth by the vendor.
              </em>
            </p>

            <h4 className="font-semibold">Initiating a Return Request</h4>
            <p>
              If your item qualifies for a return based on the above conditions, please follow these
              steps:
            </p>
            <ol className="list-decimal pl-5 space-y-2">
              <li>
                <strong>Contact Support:</strong> Reach out to our customer support team within the
                specified timeframe (usually within 48 hours of delivery, or as mentioned on the
                product page).
              </li>
              <li>
                <strong>Provide Details:</strong> Have your Order ID ready and provide clear
                photographic or video evidence of the product&apso;s condition or discrepancy.
              </li>
              <li>
                <strong>Await Approval:</strong> Our team, in conjunction with the vendor, will
                review your request and communicate the next steps.
              </li>
            </ol>

            <h4 className="font-semibold">Refund Process for Approved Returns</h4>
            <p>
              Upon approval of your return request by the vendor, we will proceed with the refund
              according to the following guidelines:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong>Processing Timeline:</strong> Refunds are typically processed within{' '}
                <strong>7 business days</strong> from the date of the return approval. This period
                may vary based on individual vendor terms.
              </li>
              <li>
                <strong>Currency:</strong> All refunds are processed and issued in{' '}
                <strong>Indian Rupees (INR)</strong>.
              </li>
            </ul>
            <div className="my-4 rounded-md border-l-4 border-yellow-500 bg-yellow-50 p-4 dark:bg-yellow-900/20">
              <h5 className="font-bold text-yellow-800 dark:text-yellow-300">Important Note:</h5>
              <p className="text-yellow-700 dark:text-yellow-200">
                BigLocal.in reserves the right to make final decisions on all return and refund
                requests. Our goal is to provide fair and transparent resolutions while adhering to
                vendor policies.
              </p>
            </div>

            {/* --- New Refund & Cancellation Policy Section --- */}
            <h3 className="font-bold text-gray-700 dark:text-gray-300">
              Refund & Cancellation Policy
            </h3>

            <h4 className="font-semibold">Order Cancellations</h4>
            <p>
              The ability to cancel an order is subject to approval by the respective BigLocal.in
              vendor. Below are the terms for different cancellation scenarios:
            </p>
            <ul className="list-none pl-0 space-y-4">
              <li>
                <h5 className="font-semibold">1. Cancellations Initiated by You</h5>
                <p>
                  You may request to cancel an order after it has been placed. If the vendor
                  approves the cancellation, any applicable refund will be processed to the original
                  payment method within <strong>7 business days</strong>, unless otherwise specified
                  in individual product policies or vendor terms.
                </p>
              </li>
              <li>
                <h5 className="font-semibold">2. Cancellations by a BigLocal.in Vendor</h5>
                <p>
                  Our vendors reserve the right to cancel an order under certain circumstances,
                  including but not limited to: suspected fraud, violations of our Terms of Service,
                  product unavailability, or other unforeseen events. If your order is cancelled by
                  a vendor, any applicable refund will be processed to the original payment method
                  within <strong>7 business days</strong>, unless otherwise specified in individual
                  product policies or vendor terms.
                </p>
              </li>
              <li>
                <h5 className="font-semibold">3. Cancellations Due to Errors</h5>
                <p>
                  In the event of an error in product information or pricing, we reserve the right
                  to cancel the order. Should this occur, a refund will be processed to the original
                  payment method within <strong>7 business days</strong>, unless otherwise specified
                  in individual product policies or vendor terms.
                </p>
              </li>
            </ul>
            <div className="my-4 rounded-md border-l-4 border-sky-500 bg-sky-50 p-4 dark:bg-sky-900/20">
              <h5 className="font-bold text-sky-800 dark:text-sky-300">Important Terms</h5>
              <ul className="list-disc pl-5 space-y-2 text-sky-700 dark:text-sky-200">
                <li>
                  <strong>Currency:</strong> All refunds will be processed and issued in Indian
                  Rupees (INR).
                </li>
                <li>
                  <strong>Promotional Offers:</strong> If an order is cancelled or refunded, any
                  promotional offers, discounts, or codes applied to that order may be voided and
                  may not be reusable.
                </li>
                <li>
                  <strong>Final Discretion:</strong> BigLocal.in and its vendors reserve the right
                  to make the final decision on all refund and cancellation requests.
                </li>
              </ul>
            </div>

            {/* --- Existing Content Resumes Here --- */}
            <h3 className="font-bold text-gray-700 dark:text-gray-300">Governing Law</h3>
            <p>These Terms are governed by and construed in accordance with the laws of India.</p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">Dispute Resolution</h3>
            <p>
              Any disputes arising out of or relating to these Terms will be resolved by the courts
              of Delhi, India.
            </p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">Entire Agreement</h3>
            <p>
              These Terms constitute the entire agreement between you and BigLocal.in regarding your
              use of the Platform.
            </p>
          </div>
        </div>
      </main>
      <BottomNav />
    </>
  );
}
